import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useHeroParallax } from '@/hooks/useHeroParallax';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import { CASE_POSTS } from '@/data/mockData';
import s from './CasesList.module.scss';

// 설치 사례 목록 — /cases.
//   [2026-10-11 사용자] 기획서 v7 §6-1(①분류=환경·안전·A/S 탭)대로 3탭으로 재구성.
//     이전엔 안전/환경 2탭뿐이라 서브메뉴 'A/S·개선사례'(?tab=environment)가 '환경'으로 흡수→브레드크럼도 '환경 설치사례'로 오표기됨.
//     데이터: CasePost.category = '설치' | 'AS'. 안전=domain safety / 환경=environment·설치 / A/S=category AS(전 도메인).
//   ?tab=safety|environment|as 로 진입. 뒤로가기 스크롤 복원=react-router Link. [[evs-cases-architecture]]
const CASES_HERO = '/images/hero/cases-hero.jpg';
const CASES_HERO_ENV = '/images/hero/cases-hero-env.jpg';

type CaseView = 'safety' | 'environment' | 'as';
const VIEW_LABEL: Record<CaseView, string> = { safety: '안전 설치사례', environment: '환경 설치사례', as: 'A/S·개선사례' };
// [2026-10-11 사용자] 서브(탭)마다 히어로 제목·슬로건이 동일하던 문제 → 분류별 의미에 맞게 차별화(이미지는 메인메뉴당 1개 유지).
const HERO_SUB: Record<CaseView, string> = {
  safety: '건설·제조 현장의 온열질환 예방 — AI 체감온도계(WBGT) 설치·운영 사례입니다.',
  environment: '대기배출 사업장의 방지·배출시설 IoT 측정기기 설치·운영 사례입니다.',
  as: '현장 장애의 원인 진단과 조치·개선 결과를 담은 A/S 사례입니다.',
};
const isView = (v: string | null): v is CaseView => v === 'safety' || v === 'environment' || v === 'as';

export default function CasesList() {
  const heroBgRef = useHeroParallax<HTMLDivElement>();
  const [params] = useSearchParams();
  const tabParam = params.get('tab');
  const [view, setView] = useState<CaseView>(isView(tabParam) ? tabParam : 'safety');
  useEffect(() => { if (isView(tabParam)) setView(tabParam); }, [tabParam]);

  // 뷰별 필터: 안전=안전 도메인 / 환경=환경 도메인의 '설치'만 / A/S=category 'AS'(설치와 분리).
  const match = (p: typeof CASE_POSTS[number], v: CaseView) =>
    v === 'safety' ? p.domain === 'safety'
      : v === 'environment' ? (p.domain === 'environment' && p.category === '설치')
        : p.category === 'AS';
  const counts = useMemo(() => ({
    safety: CASE_POSTS.filter((p) => match(p, 'safety')).length,
    environment: CASE_POSTS.filter((p) => match(p, 'environment')).length,
    as: CASE_POSTS.filter((p) => match(p, 'as')).length,
  }), []);
  const posts = useMemo(() => CASE_POSTS.filter((p) => match(p, view)), [view]);

  return (
    <main className={`${s.page} ${view === 'safety' ? s.tSafety : s.tEnv}`}>
      <PageLoader />
      <header className={s.hero}>
        <div ref={heroBgRef} className={s.heroBg} style={{ backgroundImage: `url(${view === 'safety' ? CASES_HERO : CASES_HERO_ENV})` }} />
        <div className={s.heroOverlay} />
        {/* [2026-10-11] 활성 탭(view)을 브레드크럼 최종 단계로 반영(안전/환경/A/S 각각). */}
        <HeroCrumbs items={['HOME', '설치 사례', VIEW_LABEL[view]]} />
        <div className={s.heroInner}>
          <h1 className={s.title}>{VIEW_LABEL[view]}</h1>
          <p className={s.sub}>{HERO_SUB[view]}</p>
        </div>
      </header>

      <div className={s.inner}>
        {/* 안전 / 환경 / A/S 세그먼트 토글 — 디폴트 안전 (기획서 v7 §6-1 ①분류) */}
        <div className={s.tabs} role="tablist" aria-label="설치 사례 분류">
          <button type="button" role="tab" aria-selected={view === 'safety'}
            className={`${s.tab} ${view === 'safety' ? s.on : ''}`} onClick={() => setView('safety')}>
            안전 <span>{counts.safety}</span>
          </button>
          <button type="button" role="tab" aria-selected={view === 'environment'}
            className={`${s.tab} ${s.envTab} ${view === 'environment' ? s.on : ''}`} onClick={() => setView('environment')}>
            환경 <span>{counts.environment}</span>
          </button>
          <button type="button" role="tab" aria-selected={view === 'as'}
            className={`${s.tab} ${s.envTab} ${view === 'as' ? s.on : ''}`} onClick={() => setView('as')}>
            A/S·개선 <span>{counts.as}</span>
          </button>
        </div>

        <div className={s.grid}>
          {posts.map((p) => (
            <Link key={p.slug} to={`/cases/${p.slug}`} className={s.card}>
              <div className={s.media}><img src={p.cover} alt={p.title} loading="lazy" /></div>
              <span className={`${s.cat} ${p.domain === 'environment' ? s.env : ''}`}>{p.domain === 'safety' ? '안전' : '환경'} · {p.category === 'AS' ? 'A/S' : '설치'} 사례</span>
              <h3 className={s.cardTitle}>{p.title}</h3>
              <span className={s.date}>{p.date}</span>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
